import React, { useRef } from "react";
import {
  copyRequestPath,
  defaultErrorMessage
} from "protocol-setup/redux/constants";
import { postWithAuth } from "protocol-setup/redux/makeFetch";
import {
  OpenErrorNotification,
  OpenSuccessNotification,
  openConfirmAlert
} from "@ctsu/react-ctsu-ui";
import { isNil } from "ramda";
import {
  useValidateCopyRequest,
  useFindCurrentActiveRequest
} from "protocol-setup/redux/useLovs";
import { FasPlus } from "assets/icons";
import ExistingRequestChangeMessage from "./ExistingRequestChangeMessage";

const parseValidationResponse = (validationRes) => {
  if (typeof validationRes !== "string") {
    return [];
  }

  const parseRequestAndStatus = (text) => {
    const trimmedText = String(text || "").trim();
    const requestMatch = trimmedText.match(/^([^\s(]+)/);
    const statusMatch = trimmedText.match(/\(([^)]+)\)/);

    return [requestMatch?.[1], statusMatch?.[1]].filter(
      (item) => !isNil(item) && String(item).trim() !== ""
    );
  };

  const normalizedLines = validationRes
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);

  if (normalizedLines.length > 1) {
    const firstToken = normalizedLines[0].split(/\s+/)[0];

    if (firstToken === "SUCCESS" || firstToken === "ERROR") {
      return [firstToken, ...parseRequestAndStatus(normalizedLines[1])];
    }

    return normalizedLines;
  }

  const firstLine = normalizedLines[0] || "";
  const firstToken = firstLine.split(/\s+/)[0];

  if (firstToken === "SUCCESS" || firstToken === "ERROR") {
    const remainingText = firstLine.replace(/^\w+\s*/, "");

    return [firstToken, ...parseRequestAndStatus(remainingText)];
  }

  return normalizedLines;
};

const CreateRequestChangeCore = ({
  requestNumber,
  formPartType,
  fetchPsData,
  history,
  protocolNumber,
  lpo,
  requestStatus,
  item,
  getIcon
}) => {
  const { validateCopyRequest } = useValidateCopyRequest(
    requestNumber,
    formPartType
  );

  const buttonRef = useRef(null);

  const handleRequestSubmit = async (reqNum) => {
    try {
      const res = await postWithAuth(copyRequestPath, {
        requestNumber: reqNum
      });
      if (
        res &&
        typeof res === "string" &&
        res.split("\n")[0] === "SUCCESS" &&
        !isNil(res.split("\n")[1])
      ) {
        OpenSuccessNotification({
          message: "Request created successfully.",
          autoHide: true
        });
        await fetchPsData();
        if (history && history.push) {
          history.push(
            `/protocol-setup/${formPartType === "OPEN_RAVE" ? "open-rave" : "regulatory"}/${res.split("\n")[1]}`
          );
        }
      } else {
        OpenErrorNotification({
          message: defaultErrorMessage,
          autoHide: true
        });
        if (history && history.push) {
          history.push("/protocol-setup/browser");
        }
      }
    } catch (error) {
      OpenErrorNotification({
        message: defaultErrorMessage,
        autoHide: true
      });
    }
  };

  const handleClick = async () => {
    const validationRes = await validateCopyRequest();
    const statusArr = parseValidationResponse(validationRes);


    const isSuccess = (['NEW', 'DRAFT'].indexOf(requestStatus) === -1 && statusArr?.[0] === "SUCCESS");


    openConfirmAlert({
      title: (
        <div className="d-flex align-items-center">
          <FasPlus className="mr-1 color-turquoise" />
          <span>Create Request Change</span>
        </div>
      ),
      message: isSuccess ? (
        <div>
          <p>
            Please confirm you would like to create a{" "}
            {`${formPartType === "OPEN_RAVE" ? "OPEN/Rave" : "Regulatory"}`}{" "}
            request change. Configurations will be copied from the most recently
            Submitted request change for &quot;
            <strong>{`${formPartType === "OPEN_RAVE" ? "OPEN/Rave" : "Regulatory"} | ${lpo} | ${protocolNumber}`}</strong>
            &quot;.
          </p>
          <p>If you did not mean to create a request change, please Cancel.</p>
        </div>
      ) : (
        <ExistingRequestChangeMessage
          formPartType={formPartType}
          lpo={lpo}
          protocolNumber={protocolNumber}
          requestStatus={requestStatus}
          requestNumber={requestNumber}
          statusArr={statusArr}
        />
      ),
      buttons: [
        ...(isSuccess
          ? [
            {
              label: "Confirm",
              onClick: () => handleRequestSubmit(requestNumber),
              className: "btn turquoise"
            }
          ]
          : []),
        {
          label: isSuccess ? "Cancel" : "Close",
          onClick: () => {
            if (buttonRef.current) {
              setTimeout(() => {
                buttonRef.current.focus();
              }, 0);
            }
          },
          className: "close"
        }
      ]
    });
  };

  return (
    <button
      ref={buttonRef}
      type="button"
      disabled={item.disabled}
      onClick={handleClick}
      className="btn mx-1 summary-bar-item"
    >
      {item.displayBefore && (
        <span
          data-requestnumber={`${item.requestNumber}-summary-${item.key}`}
          className="summary-bar-key"
        >
          {getIcon(item.icon)}
        </span>
      )}
      <span
        data-requestnumber={`${item.requestNumber}-summary-${item.value.split(" ")[0]
          }`}
        className="summary-bar-value"
      >
        {item.value}
      </span>
      {!item.displayBefore && (
        <span
          data-requestnumber={`${item.requestNumber}-summary-${item.key}`}
          className="summary-bar-key"
        >
          {getIcon(item.icon)}
        </span>
      )}
    </button>
  );
};

const CreateRequestChangeRetired = ({
  requestNumber,
  formPartType,
  fetchPsData,
  history,
  protocolNumber,
  lpo,
  item,
  getIcon
}) => {
  const currentRequest = useFindCurrentActiveRequest({
    requestNumber,
    protocolNumber,
    lpo,
    formPartType
  });

  if (!currentRequest) {
    return null;
  }

  return (
    <CreateRequestChangeCore
      requestNumber={currentRequest.requestNumber}
      formPartType={formPartType}
      fetchPsData={fetchPsData}
      history={history}
      protocolNumber={protocolNumber}
      lpo={lpo}
      requestStatus={currentRequest.requestStatus}
      item={item}
      getIcon={getIcon}
    />
  );
};

const CreateRequestChange = ({
  requestNumber,
  formPartType,
  fetchPsData,
  history,
  protocolNumber,
  lpo,
  requestStatus,
  item,
  getIcon
}) => {
  if (requestStatus === "RETIRED") {
    return (
      <CreateRequestChangeRetired
        requestNumber={requestNumber}
        formPartType={formPartType}
        fetchPsData={fetchPsData}
        history={history}
        protocolNumber={protocolNumber}
        lpo={lpo}
        requestStatus={requestStatus}
        item={item}
        getIcon={getIcon}
      />
    );
  }
  return (
    <CreateRequestChangeCore
      requestNumber={requestNumber}
      formPartType={formPartType}
      fetchPsData={fetchPsData}
      history={history}
      protocolNumber={protocolNumber}
      lpo={lpo}
      requestStatus={requestStatus}
      item={item}
      getIcon={getIcon}
    />
  );
};

export default CreateRequestChange;
